/* eslint-disable no-console */
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import { getCreditos } from "../../utils/creditos";
import { useCurrency } from "../../utils/useCurrency";
import { Header } from "../Header";
import CrearCreditoModal from "./CrearCreditoModal";
import DetalleCreditoModal from "./DetalleCreditoModal";
import styles from "./Creditos.module.scss";

const FILTRO_OPCIONES = [
  { value: "activos", label: "Activos" },
  { value: "liquidados", label: "Liquidados" },
  { value: "todos", label: "Todos" },
];

const TIPO_ACREEDOR_LABEL = {
  banco: "Banco",
  privado: "Privado",
  casa_matriz: "Casa matriz",
  intercompany: "Intercompany",
  socio: "Socio",
};

const formatFechaCorta = (fechaStr) => {
  if (!fechaStr) return "—";
  const f = fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
  const [, m, d] = f.split("-");
  return `${d}/${m}`;
};

// Métricas derivadas de las cuotas de un crédito.
const calcularResumenCredito = (credito) => {
  const cuotas = credito.cuotas || [];
  const pagadas = cuotas.filter((c) => c.estado === "pagada");
  const pendientes = cuotas.filter((c) => c.estado !== "pagada");
  const saldoPendiente = pendientes.reduce((acc, c) => acc + Number(c.monto_total || 0), 0);
  const fechasPendientes = pendientes.map((c) => c.fecha_vencimiento);
  fechasPendientes.sort((a, b) => (a || "").localeCompare(b || ""));
  const proximaCuota = fechasPendientes[0];
  // Monto de cuota "típico": fijo si existe, si no la primera cuota fuera de gracia.
  const tieneCuotaFija = credito.monto_cuota_fijo != null && credito.monto_cuota_fijo !== "";
  const primeraConMonto = pendientes.find((c) => Number(c.monto_total) > 0);
  const montoVariable = primeraConMonto?.monto_total ?? cuotas[0]?.monto_total ?? 0;
  const montoCuota = tieneCuotaFija ? Number(credito.monto_cuota_fijo) : montoVariable;
  return {
    cuotasPagadas: pagadas.length,
    cuotasTotal: cuotas.length,
    saldoPendiente,
    proximaCuota,
    montoCuota,
  };
};

const Creditos = () => {
  const { pais, id: userId } = useAuthContext();
  const { formatAmount } = useCurrency();

  const [creditos, setCreditos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState("activos");
  const [crearModal, setCrearModal] = useState(false);
  const [detalleId, setDetalleId] = useState(null);

  const cargar = async () => {
    if (!pais) return;
    setLoading(true);
    try {
      const data = await getCreditos(pais);
      setCreditos(data);
    } catch (err) {
      console.error("Error cargando créditos:", err);
      toast.error("Error cargando créditos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais]);

  const creditosFiltrados = useMemo(() => {
    if (filtro === "todos") return creditos;
    const estado = filtro === "liquidados" ? "liquidado" : "activo";
    return creditos.filter((c) => c.estado === estado);
  }, [creditos, filtro]);

  const detalleCredito = useMemo(() => creditos.find((c) => c.id === detalleId) || null, [creditos, detalleId]);

  const resumenGlobal = useMemo(() => {
    let saldoTotal = 0;
    const activos = creditos.filter((c) => c.estado === "activo");
    activos.forEach((c) => {
      saldoTotal += calcularResumenCredito(c).saldoPendiente;
    });
    return {
      total: creditos.length,
      activos: activos.length,
      liquidados: creditos.filter((c) => c.estado === "liquidado").length,
      saldoTotal,
    };
  }, [creditos]);

  return (
    <div className={styles.wrapper}>
      <Header
        centerContent={
          <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center" }}>
            <select
              className="form-select form-select-sm"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              style={{ width: 160, fontSize: "0.8rem", textAlign: "left" }}
              title="Filtro de estado"
            >
              {FILTRO_OPCIONES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        }
      >
        <button type="button" className={styles.btnNuevo} onClick={() => setCrearModal(true)}>
          + Nuevo crédito
        </button>
      </Header>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.empty}>Cargando…</div>
        ) : (
          <>
            <div className={styles.cardsSection}>
              <div className={styles.card}>
                <div className={styles.cardLabel}>Créditos activos</div>
                <div className={styles.cardValue}>{resumenGlobal.activos}</div>
              </div>
              <div className={styles.card}>
                <div className={styles.cardLabel}>Saldo pendiente (activos)</div>
                <div className={styles.cardValue}>
                  {formatAmount(resumenGlobal.saldoTotal, pais === "Perú" ? "PEN" : "CLP")}
                </div>
              </div>
              <div className={styles.card}>
                <div className={styles.cardLabel}>Liquidados</div>
                <div className={styles.cardValue}>{resumenGlobal.liquidados}</div>
              </div>
              <div className={styles.card}>
                <div className={styles.cardLabel}>Total</div>
                <div className={styles.cardValue}>{resumenGlobal.total}</div>
              </div>
            </div>

            {creditosFiltrados.length === 0 ? (
              <div className={styles.empty}>Sin créditos registrados.</div>
            ) : (
              <div className={styles.tableContainer}>
                <div className={styles.tableResponsive}>
                  <table className={`table table-hover ${styles.table}`}>
                    <thead>
                      <tr>
                        <th className={styles.colNombre}>Nombre</th>
                        <th className={styles.colAcreedor}>Acreedor</th>
                        <th className={styles.colTipo}>Tipo</th>
                        <th className={styles.colMonto}>Monto original</th>
                        <th className={styles.colMoneda}>Moneda</th>
                        <th className={styles.colCuotas}>Cuotas</th>
                        <th className={styles.colCuota}>Monto cuota</th>
                        <th className={styles.colProxima}>Próxima</th>
                        <th className={styles.colSaldo}>Saldo pend.</th>
                        <th className={styles.colEstado}>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditosFiltrados.map((c) => {
                        const r = calcularResumenCredito(c);
                        return (
                          <tr key={c.id} onClick={() => setDetalleId(c.id)}>
                            <td className={styles.colNombre} title={c.nombre}>
                              {c.nombre}
                            </td>
                            <td className={styles.colAcreedor} title={c.acreedor_nombre}>
                              {c.acreedor_nombre}
                            </td>
                            <td className={styles.colTipo}>
                              {TIPO_ACREEDOR_LABEL[c.tipo_acreedor] || c.tipo_acreedor}
                            </td>
                            <td className={styles.colMonto}>{formatAmount(c.monto_original, c.moneda)}</td>
                            <td className={styles.colMoneda}>{c.moneda}</td>
                            <td className={styles.colCuotas}>
                              {r.cuotasPagadas}/{r.cuotasTotal}
                            </td>
                            <td className={styles.colCuota}>{formatAmount(r.montoCuota, c.moneda)}</td>
                            <td className={styles.colProxima}>{formatFechaCorta(r.proximaCuota)}</td>
                            <td className={styles.colSaldo}>{formatAmount(r.saldoPendiente, c.moneda)}</td>
                            <td className={styles.colEstado}>
                              <span
                                className={`${styles.estadoBadge} ${
                                  c.estado === "liquidado" ? styles.estadoLiquidado : styles.estadoActivo
                                }`}
                              >
                                {c.estado === "liquidado" ? "Liquidado" : "Activo"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <CrearCreditoModal
        isOpen={crearModal}
        onClose={() => setCrearModal(false)}
        paisDefault={pais}
        userId={userId}
        onSuccess={cargar}
      />
      <DetalleCreditoModal credito={detalleCredito} onClose={() => setDetalleId(null)} onRefresh={cargar} />
    </div>
  );
};

export default Creditos;
